import React, { useState, useEffect, useRef } from 'react';
import { Sparkles, CheckCircle2, Clock } from 'lucide-react';

const ESTIMATED_TOTAL_SECONDS = 59;

export default function Global502Modal() {
  const [isUpdating, setIsUpdating] = useState(false);
  const [countdown, setCountdown] = useState(ESTIMATED_TOTAL_SECONDS);
  const [isReconnected, setIsReconnected] = useState(false);
  const isUpdatingRef = useRef(false);

  // Synchronize ref
  useEffect(() => {
    isUpdatingRef.current = isUpdating;
  }, [isUpdating]);

  const triggerUpdatingState = () => {
    if (!isUpdatingRef.current) {
      setIsUpdating(true);
      isUpdatingRef.current = true;
      setCountdown(ESTIMATED_TOTAL_SECONDS);
      setIsReconnected(false);
    }
  };

  // 1. Periodic check for explicit predeploy flag from server (only when tab is visible and not already updating)
  useEffect(() => {
    const isCheckingRef = { current: false };

    const checkDeploymentFlag = async () => {
      if (isUpdatingRef.current || isCheckingRef.current) return;
      if (typeof document !== 'undefined' && document.hidden) return; // Don't ping if tab is in background

      isCheckingRef.current = true;
      try {
        const res = await fetch(`/health/live?_t=${Date.now()}`, {
          method: 'GET',
          cache: 'no-store',
          headers: { 'Accept': 'application/json' }
        });
        if (res.ok) {
          const data = await res.json().catch(() => ({}));
          if (data && data.is_deploying === true) {
            triggerUpdatingState();
          }
        }
      } catch (e) {
        // Network or saturation hiccups are ignored - DO NOT trigger modal on random network errors!
      } finally {
        isCheckingRef.current = false;
      }
    };

    const interval = setInterval(checkDeploymentFlag, 10000);
    const handleVisibilityChange = () => {
      if (!document.hidden) checkDeploymentFlag();
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // 2. Countdown timer while updating (59s down to 0s)
  useEffect(() => {
    let timer;
    if (isUpdating && !isReconnected) {
      timer = setInterval(() => {
        setCountdown((prev) => (prev > 0 ? prev - 1 : 0));
      }, 1000);
    }
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [isUpdating, isReconnected]);

  // 3. Active Reconnection Check while updating (pings /health/ready & /health/live every 1.5s)
  useEffect(() => {
    let checkInterval;
    if (isUpdating) {
      checkInterval = setInterval(async () => {
        try {
          const res = await fetch(`/health/ready?_t=${Date.now()}`, {
            method: 'GET',
            cache: 'no-store',
            headers: { 'Accept': 'application/json' }
          });
          if (res.ok) {
            // Also check if is_deploying is cleared on live endpoint
            const liveRes = await fetch(`/health/live?_t=${Date.now()}`, {
              method: 'GET',
              cache: 'no-store',
              headers: { 'Accept': 'application/json' }
            }).catch(() => null);

            let stillDeploying = false;
            if (liveRes && liveRes.ok) {
              const liveData = await liveRes.json().catch(() => ({}));
              if (liveData && liveData.is_deploying === true) {
                stillDeploying = true;
              }
            }

            if (!stillDeploying) {
              setIsReconnected(true);
              clearInterval(checkInterval);
              // Clean reload after 600ms to load new Vite chunks seamlessly
              setTimeout(() => {
                window.location.reload();
              }, 600);
            }
          }
        } catch (e) {
          // Still waiting for container startup
        }
      }, 1500);
    }

    return () => {
      if (checkInterval) clearInterval(checkInterval);
    };
  }, [isUpdating]);

  if (!isUpdating) return null;

  const progressPercent = Math.max(0, Math.min(100, ((ESTIMATED_TOTAL_SECONDS - countdown) / ESTIMATED_TOTAL_SECONDS) * 100));

  return (
    <div className="fixed inset-0 z-[99999999] bg-slate-950/92 backdrop-blur-2xl flex flex-col items-center justify-center p-4 select-none animate-in fade-in duration-300">
      {/* Ambient glowing background circles */}
      <div className="absolute w-[500px] h-[500px] bg-indigo-600/20 rounded-full blur-3xl pointer-events-none -top-24 -left-24 animate-pulse" />
      <div className="absolute w-[500px] h-[500px] bg-blue-600/20 rounded-full blur-3xl pointer-events-none -bottom-24 -right-24 animate-pulse" style={{ animationDelay: '1s' }} />

      <div className="bg-slate-900/95 border border-slate-800/90 rounded-3xl p-8 sm:p-10 max-w-lg w-full text-center shadow-2xl relative overflow-hidden transform scale-100 animate-in zoom-in-95 duration-300">
        {/* Top gradient accent line */}
        <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500" />

        {/* Badge Header */}
        <div className="inline-flex items-center gap-2 px-3.5 py-1 rounded-full bg-indigo-950/80 border border-indigo-700/60 text-indigo-300 text-xs font-bold uppercase tracking-wider mb-7 shadow-inner">
          <Sparkles className="w-3.5 h-3.5 text-indigo-400 animate-spin" style={{ animationDuration: '4s' }} />
          <span>Actualización del Sistema</span>
        </div>

        {/* Central Focal Countdown Hub */}
        <div className="flex justify-center mb-6">
          <div className="relative flex items-center justify-center">
            {/* Outer pulsating ring */}
            <div className={`absolute inset-0 rounded-full blur-md transition-all duration-500 ${
              isReconnected ? 'bg-emerald-500/30' : 'bg-indigo-500/25 animate-pulse'
            }`} />

            {/* Main Center Circle */}
            <div className={`w-40 h-40 sm:w-44 sm:h-44 rounded-full flex flex-col items-center justify-center border shadow-2xl relative z-10 transition-all duration-500 ${
              isReconnected
                ? 'bg-gradient-to-b from-emerald-950/90 to-slate-900 border-emerald-500/50 text-emerald-400'
                : 'bg-gradient-to-b from-slate-800/90 to-slate-950/95 border-indigo-500/40 text-white'
            }`}>
              {/* Subtle spinning border effect */}
              {!isReconnected && (
                <div className="absolute inset-0 rounded-full border-2 border-indigo-500/20 border-t-indigo-400 animate-spin pointer-events-none" style={{ animationDuration: '6s' }} />
              )}

              {isReconnected ? (
                <div className="flex flex-col items-center justify-center animate-in zoom-in duration-300">
                  <CheckCircle2 className="w-16 h-16 text-emerald-400 mb-1 drop-shadow-[0_0_12px_rgba(52,211,153,0.5)]" />
                  <span className="text-xs font-black tracking-widest text-emerald-300 uppercase">Listo</span>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center text-center">
                  <div className="flex items-baseline justify-center">
                    <span className="text-5xl sm:text-6xl font-black font-mono tracking-tighter text-transparent bg-clip-text bg-gradient-to-b from-white via-indigo-100 to-indigo-300 drop-shadow-[0_0_16px_rgba(99,102,241,0.3)]">
                      {countdown > 0 ? countdown : '0'}
                    </span>
                    <span className="text-base sm:text-lg font-bold text-indigo-300 ml-1.5 font-mono">
                      segs
                    </span>
                  </div>
                  <span className="text-xs sm:text-sm font-semibold text-indigo-300/90 tracking-wide mt-0.5">
                    {countdown > 0 ? 'restantes' : 'finalizando...'}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Title & Subtitle */}
        <h1 className="text-2xl sm:text-3xl font-black text-white mb-2 tracking-tight leading-tight">
          {isReconnected ? '¡GEMA Actualizado con Éxito!' : 'GEMA se está actualizando'}
        </h1>
        <p className="text-xs sm:text-sm text-slate-300 mb-6 leading-relaxed px-2">
          {isReconnected
            ? 'El servidor ya está en línea. Recargando la aplicación...'
            : 'Estamos desplegando una nueva versión con mejoras y optimizaciones. El servicio se restablecerá automáticamente.'}
        </p>

        {/* Visual Progress Bar */}
        {!isReconnected && (
          <div className="bg-slate-950/70 border border-slate-800/80 rounded-2xl p-3.5 mb-6 shadow-inner">
            <div className="flex items-center justify-between text-xs font-semibold text-slate-400 mb-2 px-1">
              <span className="flex items-center gap-1.5 text-slate-400">
                <Clock className="w-3.5 h-3.5 text-indigo-400" />
                Tiempo estimado: ~1 minuto
              </span>
              <span className="font-mono text-xs font-bold text-indigo-300">
                {Math.round(progressPercent)}%
              </span>
            </div>

            <div className="w-full bg-slate-800 rounded-full h-2 overflow-hidden border border-slate-700/50">
              <div
                className="bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500 h-full rounded-full transition-all duration-1000 ease-linear shadow-[0_0_8px_rgba(99,102,241,0.5)]"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
          </div>
        )}

        {/* Reconnect Status Bar */}
        <div className={`flex items-center justify-center gap-2.5 py-3 px-5 rounded-xl border text-xs font-medium transition-colors ${
          isReconnected
            ? 'bg-emerald-950/60 border-emerald-700/60 text-emerald-300'
            : 'bg-slate-950/60 border-slate-800 text-slate-400'
        }`}>
          {isReconnected ? (
            <>
              <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
              <span>Conexión restablecida. Iniciando nueva versión...</span>
            </>
          ) : (
            <>
              <span className="w-2 h-2 rounded-full bg-indigo-500 animate-pulse shrink-0" />
              <span>Comprobando disponibilidad del servidor cada segundo...</span>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
